import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, Mail, Package, Sparkles, ShoppingCart } from "lucide-react";

import { fetchCredits, purchaseBundle, fetchBundleStatus } from "@/lib/api";
import { usePublicConfig } from "@/lib/usePublicConfig";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";

function fmt(cents) {
  return `€${(cents / 100).toFixed(2).replace(".", ",")}`;
}

export default function Credits() {
  const cfg = usePublicConfig();
  const { toast } = useToast();
  const [params] = useSearchParams();
  const bundleId = params.get("bundle");
  const selectedPackId = params.get("pack") || "pack-3";

  const [email, setEmail] = useState(localStorage.getItem("ce_credits_email") || "");
  const [credits, setCredits] = useState(null);
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [purchasing, setPurchasing] = useState(false);
  const [bundleInfo, setBundleInfo] = useState(null);
  const [packId, setPackId] = useState(selectedPackId);

  const packs = (cfg?.credit_packs || []).filter((p) => p.visible !== false);
  const activePack = packs.find((p) => p.id === packId) || packs[0];
  const bundleSize = activePack?.credits || 3;
  const bundlePrice = activePack?.price_cents || 999;
  const validity = activePack?.validity_months || 12;

  useEffect(() => {
    if (!bundleId) return;
    let interval;
    async function tick() {
      try {
        const b = await fetchBundleStatus(bundleId);
        setBundleInfo(b);
        if (b.email && !email) {
          setEmail(b.email);
          localStorage.setItem("ce_credits_email", b.email);
        }
        if (b.status === "active") {
          clearInterval(interval);
        }
      } catch (e) { /* ignore */ }
    }
    tick();
    interval = setInterval(tick, 2500);
    return () => clearInterval(interval);
  }, [bundleId, email]);

  async function lookupCredits() {
    if (!email) return;
    localStorage.setItem("ce_credits_email", email);
    try {
      const data = await fetchCredits(email);
      setCredits(data);
    } catch (err) {
      toast({ title: "Kon credits niet ophalen", variant: "destructive" });
    }
  }

  async function buyBundle() {
    if (!email) {
      toast({ title: "E-mail vereist", variant: "destructive" });
      return;
    }
    setPurchasing(true);
    try {
      const res = await purchaseBundle({ email, first_name: first || undefined, last_name: last || undefined, pack_id: packId });
      window.location.href = res.payment_url;
    } catch (err) {
      toast({
        title: "Kan betaling niet starten",
        description: err?.response?.data?.detail || "Probeer opnieuw",
        variant: "destructive",
      });
    } finally {
      setPurchasing(false);
    }
  }

  return (
    <div className="py-16 bg-secondary min-h-screen" data-testid="credits-page">
      <div className="max-w-4xl mx-auto px-6">
        <span className="text-xs font-semibold uppercase tracking-wider text-[#002FA7]">Credits</span>
        <h1 className="font-display text-5xl font-bold text-ink mt-3 tracking-tight">
          Beheer je {bundleSize}-pack credits.
        </h1>
        <p className="text-ink-soft mt-3 max-w-2xl">
          Vul je e-mailadres in om je actieve credits te zien, of koop een nieuwe bundle van {bundleSize} analyses voor {fmt(bundlePrice)}.
        </p>

        {/* Bundle status (after purchase) */}
        {bundleInfo && (
          <div className="mt-8 rounded-2xl bg-white border border-border p-6" data-testid="bundle-status">
            {bundleInfo.status === "active" ? (
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-6 w-6 text-emerald-600 flex-shrink-0" />
                <div>
                  <h3 className="font-display text-lg font-semibold text-ink">Je bundle is geactiveerd</h3>
                  <p className="text-ink-soft text-sm mt-1">
                    {bundleInfo.credits_total} credits staan klaar voor <b>{bundleInfo.email}</b>. Ze zijn 12 maanden geldig.
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-3">
                <Loader2 className="h-6 w-6 text-[#002FA7] animate-spin flex-shrink-0" />
                <div>
                  <h3 className="font-display text-lg font-semibold text-ink">Betaling wordt bevestigd…</h3>
                  <p className="text-ink-soft text-sm mt-1">Even geduld, we activeren je credits zodra MultiSafepay bevestigt.</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Lookup */}
        <div className="mt-8 rounded-2xl bg-white border border-border p-6">
          <h2 className="font-display text-xl font-semibold text-ink flex items-center gap-2">
            <Mail className="h-5 w-5 text-[#002FA7]" /> Mijn credits opzoeken
          </h2>
          <div className="mt-4 grid sm:grid-cols-[1fr_auto] gap-3">
            <div>
              <Label htmlFor="cemail">E-mailadres</Label>
              <Input id="cemail" type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="lookup-email" />
            </div>
            <div className="flex items-end">
              <button onClick={lookupCredits} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-ink text-white text-sm font-semibold" data-testid="lookup-btn">
                Opzoeken
              </button>
            </div>
          </div>

          {credits && (
            <div className="mt-6">
              <div className="flex items-center gap-3 bg-blue-50 border border-blue-100 rounded-xl p-4">
                <Sparkles className="h-5 w-5 text-[#002FA7]" />
                <span className="text-sm text-ink">
                  Je hebt <b>{credits.available}</b> actieve credit(s).
                </span>
              </div>
              {credits.available > 0 && (
                <p className="text-xs text-ink-soft mt-3">
                  Tip: start een nieuwe check en gebruik je credit-e-mail om automatisch een credit te verbruiken (in plaats van te betalen).
                </p>
              )}
            </div>
          )}
        </div>

        {/* Buy new pack */}
        <div className="mt-8 rounded-2xl bg-white border-2 border-[#002FA7] p-8 relative">
          <span className="absolute -top-3 left-8 bg-[#002FA7] text-white text-xs uppercase tracking-wider font-bold px-3 py-1 rounded-full">
            Voordeelpakket
          </span>
          {packs.length > 1 && (
            <div className="mb-5 flex flex-wrap gap-2" data-testid="pack-selector">
              {packs.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setPackId(p.id)}
                  className={`px-4 py-2 rounded-full text-xs font-semibold transition-colors ${packId === p.id ? "bg-[#002FA7] text-white" : "bg-slate-100 text-ink-soft hover:bg-slate-200"}`}
                  data-testid={`pack-btn-${p.id}`}
                >
                  {p.label.split(" — ")[0]} · {fmt(p.price_cents)}
                </button>
              ))}
            </div>
          )}
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="text-sm font-semibold text-[#002FA7] flex items-center gap-2">
                <Package className="h-4 w-4" /> {activePack?.label || "Bundle"}
              </div>
              <div className="font-display text-4xl font-bold text-ink mt-2">{fmt(bundlePrice)}</div>
              <p className="text-sm text-ink-soft mt-1">
                {bundleSize} analyses · {validity} {validity === 1 ? "maand" : "maanden"} geldig · gemiddeld {fmt(bundlePrice / bundleSize)} per check
              </p>
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-4 mt-6">
            <div>
              <Label htmlFor="bemail">E-mail *</Label>
              <Input id="bemail" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="bundle-email" />
            </div>
            <div>
              <Label>Voornaam</Label>
              <Input value={first} onChange={(e) => setFirst(e.target.value)} />
            </div>
            <div>
              <Label>Achternaam</Label>
              <Input value={last} onChange={(e) => setLast(e.target.value)} />
            </div>
          </div>
          <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-ink-soft">Veilig betalen via MultiSafepay. Geen automatische verlenging — 14 dagen voor verloop krijg je een herinneringsmail.</p>
            <button
              onClick={buyBundle}
              disabled={purchasing || !email}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#002FA7] hover:bg-[#0033B5] text-white font-semibold disabled:opacity-50"
              data-testid="buy-bundle"
            >
              {purchasing ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShoppingCart className="h-4 w-4" />}
              Koop {activePack?.label?.split(" — ")[0] || "bundle"}
            </button>
          </div>
        </div>

        <div className="mt-10 text-center">
          <Link to="/checks" className="text-[#002FA7] font-semibold">Bekijk alle 25 checkers →</Link>
        </div>
      </div>
    </div>
  );
}
